import { Link, useNavigate } from "@remix-run/react";
import { Fragment, useEffect, useState, SyntheticEvent } from 'react';
import { Menu, MenuItems, MenuItem, MenuButton, Transition } from '@headlessui/react';
import { toast, Slide } from 'react-toastify';
import {showThankYouToast, getModalElement} from '../components/utils';
import { Bars3Icon, MagnifyingGlassIcon, HomeIcon, 
  ChatBubbleBottomCenterTextIcon, UserGroupIcon, LinkIcon,
  MegaphoneIcon, InformationCircleIcon, PuzzlePieceIcon, ShieldCheckIcon } from '@heroicons/react/24/solid';
import classNames from 'classnames'

interface MenuActive {
  active: boolean;
}

const feedbackModalId = 'feedback_modal';

function showFeedbackModal(e) {
  getModalElement(feedbackModalId).showModal();
}

function closeFeedbackModal(e: SyntheticEvent) {
  e.preventDefault(); // Prevent default form submission
  getModalElement(feedbackModalId).close();
}

// https://tailwindui.com/components/application-ui/elements/dropdowns#component-1f10562f6266c9b3ef15ff792edd508e
export default function Navbar() {
  const navigate = useNavigate();
  const [feedbackText, setFeedbackText] = useState('');

  async function handleFeedbackSubmit() {
    if(!feedbackText) {
      return;
    }

    const response = await fetch("/api/feedback", {
      method: "POST",
      headers: {
        "Content-Type": "application/json"
      },
      body: JSON.stringify({
        feedbackText,
        href: window.location.href
      })
    })

    if (response.ok) {
      showThankYouToast();
      setFeedbackText(''); // clear the textarea input
    } else {
      toast('⚠ Failed to submit feedback, please try again later.', {
        position: "bottom-right",
        autoClose: 3000,
        hideProgressBar: true,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "light",
        transition: Slide
      });
    }
  }

  useEffect(() => {
    document.body.addEventListener('keydown', function(keyboardEvent) {
      if(keyboardEvent.metaKey && keyboardEvent.key === 'k') {
        navigate(`/search`);
      }
    });
  }, []);

  const menuLinkClassNames = (active: boolean) => classNames(
    'px-4 py-2 text-sm flex items-center hover:cursor-pointer',
    {'bg-gray-100 dark:bg-gray-700' : active}
  );

  return (
    // https://daisyui.com/components/navbar/
    // bg-base-100 dark:bg-slate-200
    <header className="navbar">
      <div className="flex-1 flex items-center">
        <Link to="/">
          <picture>
            <source media="(prefers-color-scheme: dark)" srcSet="/images/logos/repo_star_yellow_white.svg" />
            <source media="(prefers-color-scheme: light)" srcSet="/images/logos/repo_star_yellow.svg" />
            <img src="/images/logos/repo_star_yellow.svg" width="30" height="30" className="mr-1" alt="Related Repos logo" />
          </picture>        
        </Link>
        <Link to="/" className="font-semibold text-xl">Related Repos</Link>
      </div>
      <div className="flex gap-2">
        <Link to="/search">
          <span className="flex items-center h-9 pr-2 pl-3 rounded-md background-button-white ring-1 ring-inset ring-gray-300">
            <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
            {/* ⊞ */}
            <span className="ml-2 mr-1 flex items-center">
              <span className="text-rr-button-white-primary text-sm">Search</span>
              <span className="hidden lg:inline border dark:border-gray-400 rounded-md text-xs text-gray-400 p-0.5 ml-2">⌘K</span>
            </span>
          </span>
        </Link>

        <button className="hidden sm:block hover:cursor-pointer" onClick={showFeedbackModal}>
          <span className="flex items-center h-9 pr-2 pl-3 rounded-md background-button-white ring-1 ring-inset ring-gray-300">
            <ChatBubbleBottomCenterTextIcon className="h-5 w-5 mr-2 text-gray-400" />
            {/* Or Users icon ? */}
            <span className="text-sm text-rr-button-white-primary">Feedback</span>
          </span>
        </button>

{/* Menu , -mr-1*/}
        <Menu as="div" className="relative inline-block text-left">
          <div>
            <MenuButton aria-label="App Dropdown Menu" className="inline-flex w-full justify-center gap-x-1.5 rounded-md background-button-white px-3 py-2 text-sm font-semibold shadow-sm ring-1 ring-inset ring-gray-300">
              <Bars3Icon className=" h-5 w-5 text-gray-400" aria-hidden="true" />
            </MenuButton>
          </div>

          <Transition
            as={Fragment}
            enter="transition ease-out duration-100"
            enterFrom="transform opacity-0 scale-95"
            enterTo="transform opacity-100 scale-100"
            leave="transition ease-in duration-75"
            leaveFrom="transform opacity-100 scale-100"
            leaveTo="transform opacity-0 scale-95"
          >
            {/* remove this? focus:outline-none */}
            <MenuItems className="absolute right-0 z-10 mt-2 w-56 origin-top-right divide-y divide-gray-100 rounded-md bg-white dark:bg-gray-800  shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
              <div className="py-1">
                <MenuItem>
                  {({ active }: MenuActive) => (
                    <Link to="/" className={menuLinkClassNames(active)}>
                      <HomeIcon className="h-5 w-5 mr-2 text-gray-400" />
                      <span className="text-rr-button-white-primary">Home</span>
                    </Link>
                  )}
                </MenuItem>
                <MenuItem>
                  {({ active }: MenuActive) => (
                    <Link to="/search" className={classNames(menuLinkClassNames(active))}>
                      <MagnifyingGlassIcon className="h-5 w-5 mr-2 text-gray-400" />
                      <span className="text-rr-button-white-primary">Search</span>
                    </Link>
                  )}
                </MenuItem>
                <MenuItem>
                  {({ active }: MenuActive) => (
                    <Link to="/extension" className={classNames(menuLinkClassNames(active))}>
                      <PuzzlePieceIcon className="h-5 w-5 mr-2 text-gray-400" />
                      <span className="text-rr-button-white-primary">Browser Extension</span>
                    </Link>
                  )}
                </MenuItem>
                <MenuItem>
                  {({ active }: MenuActive) => (
                    <Link to="/bookmarklet" className={classNames(menuLinkClassNames(active))}>
                      <LinkIcon className="h-5 w-5 mr-2 text-gray-400" />
                      <span className="text-rr-button-white-primary">Bookmarklet</span>
                    </Link>
                  )}
                </MenuItem>
                <MenuItem>
                  {({ active }: MenuActive) => (
                    <Link to="/badge" className={classNames(menuLinkClassNames(active))}>
                      <ShieldCheckIcon className="h-5 w-5 mr-2 text-gray-400" />
                      <span className="text-rr-button-white-primary">Readme Badge</span>
                    </Link>
                  )}
                </MenuItem>
              </div>
              <div className="py-1">
                <MenuItem>
                  {({ active }: MenuActive) => (
                    <a onClick={showFeedbackModal} className={classNames(menuLinkClassNames(active))}>
                      <ChatBubbleBottomCenterTextIcon className="h-5 w-5 mr-2 text-gray-400" />
                      <span className="text-rr-button-white-primary">Feedback</span>
                    </a>
                  )}
                </MenuItem>
                {/* <MenuItem>
                  {({ active }: MenuActive) => (
                    <a href="/blog" className={classNames(menuLinkClassNames(active))}>
                      <MegaphoneIcon className="h-5 w-5 mr-2 text-gray-400" />
                      <span className="text-rr-button-white-primary">Blog</span>
                    </a>
                  )}
                </MenuItem> */}
                <MenuItem>
                  {({ active }: MenuActive) => (
                    <a href="https://github.com/RelatedRepos/discussion/discussions" target="_blank" className={classNames(menuLinkClassNames(active))}>
                      <UserGroupIcon className="h-5 w-5 mr-2 text-gray-400" />
                      <span className="text-rr-button-white-primary">Community</span>
                    </a>
                  )}
                </MenuItem>
              </div>
              <div className="py-1">
                <MenuItem>
                  {({ active }: MenuActive) => (
                    <Link to="/about" className={classNames(menuLinkClassNames(active))}>
                      <InformationCircleIcon className="h-5 w-5 mr-2 text-gray-400" />
                      <span className="text-rr-button-white-primary">About</span>
                    </Link>
                  )}
                </MenuItem>
              </div>
            </MenuItems>
          </Transition>
        </Menu>
      </div>

      {/* https://daisyui.com/components/modal/?lang=en */}
      <dialog id="feedback_modal" className="modal">
        <div className="modal-box">
          <h3 className="font-bold text-lg pb-3">Send Feedback</h3>
          <form method="dialog" onSubmit={handleFeedbackSubmit}>
            <label htmlFor="myTextArea" className="block">We appreciate any helpful feedback:</label>
            <textarea
              value={feedbackText}
              onChange={(e) => setFeedbackText(e.target.value)} // Update state on change
              className="block w-full my-2 border-2 border-gray-300 rounded-md p-2 focus:outline-none focus:border-slate-500"
              id="myTextArea" name="myTextArea" rows={4} ></textarea>
            
            <div className="flex justify-between">
              <button className="button-white" onClick={closeFeedbackModal}>Cancel</button>
              <input className="button-primary hover:cursor-pointer" type="submit" value="Submit" />
            </div>
          </form>
        </div>
      </dialog>

    </header>
  )
}